import React, { useState } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Clock, Table as TableIcon, ChevronDown, ChevronUp, Copy } from "lucide-react";

/**
 * Gráfico plurianual del coste de mantenimiento tecnología.
 *
 * Props:
 *   data: array de { year, distromel, horas_internas, total }
 *
 * Renderiza barras apiladas (proveedor + horas internas) + línea de total.
 * Sólo se muestra si `data` viene con al menos 2 entradas.
 */
const fmtEUR = (n) => Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 }) + " €";
const fmtEUR2 = (n) => Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

/**
 * Badge "🔄 Recomputado por Pando" con tooltip enriquecido al hover:
 *  - Fecha del análisis
 *  - Modelo usado (Sonnet 4.6 / Opus 4.8 / ...)
 *  - Coste USD del análisis
 *  - Botón "Ver análisis completo" que abre la sesión en /licitaciones/{lid}
 */
function AIRecomputedBadge({ generatedAt, model, costUsd, sessionId, licitacionId, dateLabel }) {
  const [open, setOpen] = React.useState(false);
  const MODEL_LABELS = {
    "claude-sonnet-4-6": "Claude Sonnet 4.6",
    "claude-sonnet-4-5": "Claude Sonnet 4.5",
    "claude-opus-4-8":   "Claude Opus 4.8",
    "claude-opus-4-7":   "Claude Opus 4.7",
    "claude-opus-4-6":   "Claude Opus 4.6",
    "claude-haiku-4-5":  "Claude Haiku 4.5",
  };
  const modelLabel = model ? (MODEL_LABELS[model] || model) : null;
  const detailHref = licitacionId
    ? `/licitaciones/${licitacionId}${sessionId ? `?session=${sessionId}` : ""}`
    : null;
  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        data-testid="maintenance-yearly-ai-recomputed-badge"
        role="button"
        tabIndex={0}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200 cursor-help focus:outline-none focus:ring-2 focus:ring-indigo-400"
      >
        🔄 Recomputado por Pando{dateLabel ? ` · ${dateLabel}` : ""}
      </span>
      {open && (
        <div
          data-testid="maintenance-yearly-ai-tooltip"
          className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl p-3 text-left"
          role="tooltip"
        >
          <div className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider mb-2">
            Trazabilidad del análisis
          </div>
          <div className="space-y-1.5 text-[11px]">
            {dateLabel && (
              <div className="flex justify-between" data-testid="ai-tooltip-date">
                <span className="text-gray-500">Fecha</span>
                <span className="font-semibold text-gray-800">{dateLabel}</span>
              </div>
            )}
            {modelLabel && (
              <div className="flex justify-between" data-testid="ai-tooltip-model">
                <span className="text-gray-500">Modelo</span>
                <span className="font-semibold text-gray-800">{modelLabel}</span>
              </div>
            )}
            {typeof costUsd === "number" && (
              <div className="flex justify-between" data-testid="ai-tooltip-cost">
                <span className="text-gray-500">Coste</span>
                <span className="font-mono font-semibold text-emerald-700">
                  ${costUsd.toFixed(4)} USD
                </span>
              </div>
            )}
            {sessionId && (
              <div className="flex justify-between" data-testid="ai-tooltip-session">
                <span className="text-gray-500">Sesión</span>
                <span className="font-mono text-[10px] text-gray-600 truncate max-w-[130px]" title={sessionId}>
                  {String(sessionId).slice(0, 12)}…
                </span>
              </div>
            )}
          </div>
          {detailHref && (
            <a
              href={detailHref}
              data-testid="ai-tooltip-view-analysis"
              className="mt-3 flex items-center justify-center gap-1 text-[10px] font-semibold text-indigo-700 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 rounded px-2 py-1.5 border border-indigo-200 transition-colors"
            >
              Ver análisis completo →
            </a>
          )}
          {!detailHref && !modelLabel && !costUsd && (
            <p className="mt-2 text-[10px] text-gray-400 italic">
              Sin metadata Pando disponible en esta oferta.
            </p>
          )}
        </div>
      )}
    </span>
  );
}

export function MaintenanceYearlyChart({ data }) {
  const [showTable, setShowTable] = useState(false);
  if (!Array.isArray(data) || data.length < 2) return null;

  const aiRecomputed = !!data.__meta?.aiRecomputed;
  const aiGeneratedAt = data.__meta?.aiGeneratedAt;
  const aiDateLabel = aiGeneratedAt
    ? new Date(aiGeneratedAt).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  const totalAcumulado = data.reduce((s, d) => s + (d.total || 0), 0);
  const avgAnual = totalAcumulado / data.length;
  const maxAnual = Math.max(...data.map(d => d.total || 0));
  const minAnual = Math.min(...data.map(d => d.total || 0));

  // Totales por columna para la tabla de desglose
  const tDistromel = data.reduce((s, d) => s + (Number(d.distromel) || 0), 0);
  const tHoras = data.reduce((s, d) => s + (Number(d.horas_internas) || 0), 0);
  const tTotal = data.reduce((s, d) => s + (Number(d.total) || 0), 0);

  const copyToClipboard = () => {
    const header = ["Año", "Distromel", "Horas internas", "Total"].join("\t");
    const rows = data.map(d =>
      [`Año ${d.year}`, Number(d.distromel || 0).toFixed(2), Number(d.horas_internas || 0).toFixed(2), Number(d.total || 0).toFixed(2)].join("\t")
    );
    const totalsRow = ["TOTAL", tDistromel.toFixed(2), tHoras.toFixed(2), tTotal.toFixed(2)].join("\t");
    const text = [header, ...rows, totalsRow].join("\n");
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 mb-3" data-testid="maintenance-yearly-chart">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-indigo-600" />
          <h3 className="text-sm font-bold text-gray-800 font-['Outfit']">
            Coste mantenimiento tecnología · plurianual
          </h3>
          <span className="text-[10px] text-gray-400 font-normal">
            ({data.length} años · Distromel + Horas internas)
          </span>
          {aiRecomputed && (
            <AIRecomputedBadge
              generatedAt={aiGeneratedAt}
              model={data.__meta?.aiModel}
              costUsd={data.__meta?.aiCostUsd}
              sessionId={data.__meta?.aiSessionId}
              licitacionId={data.__meta?.aiLicitacionId}
              dateLabel={aiDateLabel}
            />
          )}
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="text-gray-500">
            Total acumulado: <strong className="text-gray-800">{fmtEUR(totalAcumulado)}</strong>
          </span>
          <span className="text-gray-500">
            Medio anual: <strong className="text-gray-800">{fmtEUR(avgAnual)}</strong>
          </span>
          <span className="text-gray-500">
            Rango: <strong className="text-gray-800">{fmtEUR(minAnual)} – {fmtEUR(maxAnual)}</strong>
          </span>
        </div>
      </div>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis
              dataKey="year"
              tickFormatter={(y) => `Año ${y}`}
              tick={{ fontSize: 10, fill: "#6b7280" }}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#6b7280" }}
              tickFormatter={(v) => `${Math.round(v / 1000)}k`}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <Tooltip
              formatter={(value) => fmtEUR(value)}
              labelFormatter={(y) => `Año ${y}`}
              contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e5e7eb" }}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} iconSize={10} />
            <Bar dataKey="distromel" stackId="a" fill="#0d9488" name="Distromel" radius={[0, 0, 0, 0]} />
            <Bar dataKey="horas_internas" stackId="a" fill="#6366f1" name="Horas internas" radius={[4, 4, 0, 0]} />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#dc2626"
              strokeWidth={2}
              dot={{ fill: "#dc2626", r: 3 }}
              name="Total"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ── Toggle "Ver desglose por año" ─────────────────────────────────── */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowTable((v) => !v)}
            data-testid="maintenance-yearly-toggle-breakdown"
            className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 transition-colors"
          >
            <TableIcon size={11} />
            {showTable ? "Ocultar desglose por año" : "Ver desglose por año"}
            {showTable ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          </button>
          {showTable && (
            <button
              onClick={copyToClipboard}
              data-testid="maintenance-yearly-copy-breakdown"
              className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-800 border border-gray-200 rounded px-2 py-0.5"
              title="Copiar tabla como TSV"
            >
              <Copy size={10} /> Copiar
            </button>
          )}
        </div>
        {showTable && (
          <div className="mt-2 overflow-x-auto" data-testid="maintenance-yearly-breakdown-table">
            <table className="w-full text-[11px]">
              <thead className="bg-gray-50 text-gray-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-2 py-1.5 font-semibold">Año</th>
                  <th className="text-right px-2 py-1.5 font-semibold">
                    <span className="inline-block w-2 h-2 rounded-sm bg-teal-600 mr-1"></span>Distromel
                  </th>
                  <th className="text-right px-2 py-1.5 font-semibold">
                    <span className="inline-block w-2 h-2 rounded-sm bg-indigo-500 mr-1"></span>Horas internas
                  </th>
                  <th className="text-right px-2 py-1.5 font-semibold">
                    <span className="inline-block w-2 h-2 rounded-sm bg-red-600 mr-1"></span>Total
                  </th>
                  {data[0]?.nota && <th className="text-left px-2 py-1.5 font-semibold">Nota</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.map((d) => (
                  <tr
                    key={d.year}
                    data-testid={`maintenance-yearly-row-${d.year}`}
                    className="hover:bg-gray-50/50"
                  >
                    <td className="px-2 py-1.5 font-semibold text-gray-700">Año {d.year}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-gray-600">{fmtEUR2(d.distromel)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-indigo-700">{fmtEUR2(d.horas_internas)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold text-red-700">{fmtEUR2(d.total)}</td>
                    {d.nota && <td className="px-2 py-1.5 text-[10px] text-gray-500 italic">{d.nota}</td>}
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-300 bg-gray-50">
                <tr>
                  <td className="px-2 py-1.5 font-bold text-gray-800">TOTAL</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold text-gray-800">{fmtEUR2(tDistromel)}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold text-indigo-800">{fmtEUR2(tHoras)}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold text-red-800">{fmtEUR2(tTotal)}</td>
                  {data[0]?.nota && <td></td>}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
